// src/components/atomic-crm/activities/ActivityComposer.tsx
import { useEffect, useRef, useState } from "react";
import { useGetIdentity, useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ds/ui/button";
import { Card, CardContent } from "@/components/ds/ui/card";
import { Textarea } from "@/components/ds/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ds/ui/select";
import { StickyNote, CheckCircle2, Loader2, Send } from "lucide-react";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

type ActivityType = "note" | "task";
type Resource = "companies" | "contacts" | "deals";
type AttachType = "contact" | "company" | "deal" | "none";
type PickerOption = { id: number; label: string };

interface ActivityComposerProps {
  /** Record-scoped mode: the resource the record belongs to */
  resource?: Resource;
  /** Record-scoped mode: the record id */
  recordId?: number;
  /** Record-scoped mode: the record (used to denormalize company_id) */
  record?: any;
  /** Global mode: render the "Attach to" picker */
  showPicker?: boolean;
}

export const ActivityComposer = ({
  resource,
  recordId,
  record,
  showPicker = false,
}: ActivityComposerProps) => {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const mySalesId = identity?.id != null ? Number(identity.id) : undefined;
  const { taskPriorities } = useConfigurationContext();

  // --- ALL hooks, top of component ---
  const [type, setType] = useState<ActivityType>("note");
  const [text, setText] = useState("");
  const [priority, setPriority] = useState<string>("medium");
  const [dueDate, setDueDate] = useState<string>(
    new Date(new Date().setHours(23, 59, 0, 0)).toISOString(),
  );
  const [submitting, setSubmitting] = useState(false);
  const [attachType, setAttachType] = useState<AttachType>("contact");
  const [attachId, setAttachId] = useState<number | null>(null);
  const [options, setOptions] = useState<PickerOption[]>([]);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- debounced picker lookup ---
  useEffect(() => {
    if (!showPicker || attachType === "none") {
      setOptions([]);
      setSearching(false);
      return;
    }

    if (searchTimer.current) clearTimeout(searchTimer.current);

    searchTimer.current = setTimeout(async () => {
      setSearching(true);

      const table =
        attachType === "contact"
          ? "contacts"
          : attachType === "company"
            ? "companies"
            : "deals";

      // Per-table select + search because `contacts` has no `name` column.
      let query;
      if (attachType === "contact") {
        query = supabase
          .from("contacts")
          .select("id, first_name, last_name")
          .limit(10);
        if (search.trim()) {
          query = query.or(
            `first_name.ilike.%${search}%,last_name.ilike.%${search}%`,
          );
        }
      } else {
        query = supabase.from(table).select("id, name").limit(10);
        if (search.trim()) {
          query = query.ilike("name", `%${search}%`);
        }
      }

      const { data, error } = await query;
      setSearching(false);

      if (error) {
        setOptions([]);
        return;
      }

      setOptions(
        (data ?? []).map((row: any) => ({
          id: row.id,
          label:
            attachType === "contact"
              ? [row.first_name, row.last_name].filter(Boolean).join(" ") ||
                `#${row.id}`
              : row.name || `#${row.id}`,
        })),
      );
    }, 200);

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [showPicker, attachType, search]);
  // --- derived ---
  const pickerReady = !showPicker || attachType === "none" || attachId != null;
  const canSubmit =
    text.trim().length > 0 && !!mySalesId && !submitting && pickerReady;

  const reset = () => {
    setText("");
    setPriority("medium");
    setDueDate(new Date(new Date().setHours(23, 59, 0, 0)).toISOString());
    setType("note");
    if (showPicker) {
      setAttachType("contact");
      setAttachId(null);
      setSearch("");
      setOptions([]);
    }
  };

  /** Resolve the FK column + value + denormalized company_id. */
  const resolveAttachment = async (): Promise<{
    fkColumn: string | null;
    fkValue: number | null;
    companyId: number | null;
  }> => {
    if (showPicker) {
      if (attachType === "none" || attachId == null) {
        return { fkColumn: null, fkValue: null, companyId: null };
      }

      const fkColumn =
        attachType === "contact"
          ? "contact_id"
          : attachType === "company"
            ? "company_id"
            : "deal_id";

      let companyId: number | null = null;
      if (attachType === "company") {
        companyId = attachId;
      } else {
        const table = attachType === "contact" ? "contacts" : "deals";
        const { data } = await supabase
          .from(table)
          .select("company_id")
          .eq("id", attachId)
          .maybeSingle();
        companyId = (data as any)?.company_id ?? null;
      }

      return { fkColumn, fkValue: attachId, companyId };
    }

    // Record-scoped mode
    if (resource && recordId != null) {
      const fkColumn =
        resource === "companies"
          ? "company_id"
          : resource === "contacts"
            ? "contact_id"
            : "deal_id";

      let companyId: number | null = null;
      if (resource === "companies") {
        companyId = recordId;
      } else if (
        (resource === "deals" || resource === "contacts") &&
        record?.company_id
      ) {
        companyId = record.company_id;
      }

      return { fkColumn, fkValue: recordId, companyId };
    }

    return { fkColumn: null, fkValue: null, companyId: null };
  };

  const handleSubmit = async () => {
    if (!canSubmit || !mySalesId) return;
    setSubmitting(true);

    const { fkColumn, fkValue, companyId } = await resolveAttachment();

    const payload: Record<string, any> = {
      type,
      text: text.trim(),
      date: new Date().toISOString(),
      sales_id: mySalesId,
      company_id: companyId,
    };

    if (fkColumn && fkValue != null) {
      payload[fkColumn] = fkValue;
    }

    if (type === "task") {
      payload.due_date = dueDate;
      payload.priority = priority;
      payload.status = "todo";
    }

    const { data, error } = await supabase
      .from("crm_activities")
      .insert(payload)
      .select("id")
      .single();

    setSubmitting(false);

    if (error) {
      notify(error.message, { type: "error" });
      return;
    }

    if (data?.id) {
      await supabase
        .from("crm_activity_assignees")
        .insert({ activity_id: data.id, sales_id: mySalesId });
    }

    reset();
    await queryClient.invalidateQueries({ queryKey: ["crm_activities"] });

    notify(translate("crm.activity.added", { _: "Activity added" }), {
      type: "info",
    });
  };

  return (
    <div className="p-4 space-y-3 bg-background rounded-lg">
      {/* Type toggle */}
      <div className="flex items-center gap-1.5">
        {(["note", "task"] as const).map((k) => {
          const active = type === k;
          const Icon = k === "note" ? StickyNote : CheckCircle2;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setType(k)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium transition-colors",
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {k === "note"
                ? translate("crm.activity.type.note", { _: "Note" })
                : translate("crm.activity.type.task", { _: "Task" })}
            </button>
          );
        })}
      </div>

      {/* Attach-to picker (global mode only) */}
      {showPicker && (
        <div className="flex flex-col sm:flex-row gap-2">
          <Select
            value={attachType}
            onValueChange={(v) => {
              setAttachType(v as AttachType);
              setAttachId(null);
              setSearch("");
              setOptions([]);
            }}
          >
            <SelectTrigger className="sm:w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="contact">
                {translate("crm.activity.attach.contact", { _: "Contact" })}
              </SelectItem>
              <SelectItem value="company">
                {translate("crm.activity.attach.company", { _: "Company" })}
              </SelectItem>
              <SelectItem value="deal">
                {translate("crm.activity.attach.deal", { _: "Deal" })}
              </SelectItem>
              <SelectItem value="none">
                {translate("crm.activity.attach.none", { _: "None" })}
              </SelectItem>
            </SelectContent>
          </Select>

          {attachType !== "none" && (
            <div className="flex-1 relative">
              <input
                type="text"
                name="activity-composer-search"
                autoComplete="off"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setAttachId(null);
                }}
                placeholder={translate("crm.activity.attach.search", {
                  _: "Search...",
                })}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />

              {/* Results dropdown — only when typing and nothing picked */}
              {attachId == null && search.trim().length > 0 && (
                <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md max-h-56 overflow-auto">
                  {searching && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      {translate("crm.activity.attach.searching", {
                        _: "Searching…",
                      })}
                    </div>
                  )}
                  {!searching && options.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      {translate("crm.activity.attach.no_results", {
                        _: "No matches",
                      })}
                    </div>
                  )}
                  {!searching &&
                    options.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          setAttachId(opt.id);
                          setSearch(opt.label);
                          setOptions([]);
                        }}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-muted"
                      >
                        {opt.label}
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Text */}
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={
          type === "note"
            ? translate("crm.activity.composer.note_placeholder", {
                _: "Write a note...",
              })
            : translate("crm.activity.composer.task_placeholder", {
                _: "What needs to be done?",
              })
        }
        className="min-h-[80px] resize-y"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
        }}
      />

      {/* Task-only fields */}
      {type === "task" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              {translate("crm.task.field.due_date", { _: "Due date" })}
            </label>
            <input
              type="datetime-local"
              value={dueDate.slice(0, 16)}
              onChange={(e) =>
                setDueDate(new Date(e.target.value).toISOString())
              }
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              {translate("crm.task.field.priority", { _: "Priority" })}
            </label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {taskPriorities.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-1">
        <span className="text-[11px] text-muted-foreground">
          {type === "task"
            ? translate("crm.activity.composer.task_hint", {
                _: "Assigned to you by default.",
              })
            : translate("crm.activity.composer.note_hint", {
                _: "Adds to the timeline.",
              })}
        </span>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          size="sm"
          className="gap-1.5"
        >
          {submitting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          {submitting
            ? translate("crm.activity.composer.saving", { _: "Saving..." })
            : translate("crm.activity.composer.add", { _: "Add" })}
        </Button>
      </div>
    </div>
  );
};
